import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateInquiry } from '@/api/contact/useCreateInquiry';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { CreateInquiryRequest } from '@/models/Inquiry';
import {
  clearWaitlistDraft,
  readWaitlistDraft,
  saveWaitlistDraft,
  trackFormStartedOnce,
  trackWaitlistFunnel,
} from '@/utils/funnel/waitlistFunnel';
import { AppRoutes } from '@/utils/routes/router';

import TurnstileWidget, { turnstileSiteKey } from './TurnstileWidget';
import {
  ACCOUNT_COUNT_OPTIONS,
  ACV_OPTIONS,
  CURRENCY_OPTIONS,
  FREQUENCY_OPTIONS,
  formatWaitlistAnswers,
  toWaitlistLeadFields,
  IMPROVEMENT_OPTIONS,
  initialWaitlistAnswers,
  isAcceptedWaitlistEmail,
  RISK_PROCESS_OPTIONS,
  ROLE_OPTIONS,
  splitName,
  TEAM_SIZE_OPTIONS,
  validateWaitlistEmail,
  WaitlistAnswers,
} from './waitlistOptions';

const inputStyles = {
  backgroundColor: 'rgba(250, 250, 250, 0.05)',
  borderColor: 'rgba(250, 250, 250, 0.12)',
  color: '#fafafa',
};

type StepDef = {
  id: string;
  label: string;
  title: string;
  subtitle?: string;
  optional?: boolean;
  validate: (answers: WaitlistAnswers) => boolean;
  render: (ctx: StepRenderContext) => ReactNode;
};

type StepRenderContext = {
  answers: WaitlistAnswers;
  setAnswers: (patch: Partial<WaitlistAnswers>) => void;
  toggleMulti: (key: 'riskProcesses', value: string) => void;
  emailTouched: boolean;
  setEmailTouched: (touched: boolean) => void;
};

function OptionButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-200 ${
        selected
          ? 'border-accent bg-primary-subtle text-primary'
          : 'border-medium bg-surface-subtle text-secondary hover:border-accent hover:text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  error,
  onBlur,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  onBlur?: () => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-2 text-primary">
        {label}
        {required ? <span className="text-accent ml-0.5">*</span> : null}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        className={`w-full px-4 py-3 rounded-lg border transition-colors duration-200 focus:outline-none ${
          error ? 'focus:border-[#e8255d]' : 'focus:border-accent'
        }`}
        style={{
          ...inputStyles,
          ...(error ? { borderColor: 'rgba(232, 37, 93, 0.7)' } : null),
        }}
      />
      {error ? <p className="mt-2 text-sm text-pink">{error}</p> : null}
    </div>
  );
}

function TextAreaField({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-2 text-primary">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-lg border transition-colors duration-200 focus:outline-none focus:border-accent resize-none"
        style={inputStyles}
      />
    </div>
  );
}

const CURRENCY_SYMBOLS: Record<(typeof CURRENCY_OPTIONS)[number], string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

function PriceFields({
  currency,
  amount,
  onCurrencyChange,
  onAmountChange,
  amountId,
}: {
  currency: string;
  amount: string;
  onCurrencyChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  amountId: string;
}) {
  const nudgeAmount = (delta: number) => {
    const current = Number.parseInt(amount || '0', 10);
    const safeCurrent = Number.isFinite(current) ? current : 0;
    onAmountChange(String(Math.max(0, safeCurrent + delta)));
  };
  const symbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] ?? '';

  return (
    <div className="space-y-5">
      <div>
        <p id={`${amountId}-currency-label`} className="block text-sm font-medium mb-2 text-primary">
          Currency
        </p>
        <div
          role="group"
          aria-labelledby={`${amountId}-currency-label`}
          className="grid grid-cols-3 gap-2"
        >
          {CURRENCY_OPTIONS.map((option) => {
            const selected = currency === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onCurrencyChange(option)}
                aria-pressed={selected}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                  selected
                    ? 'border-accent bg-primary-subtle text-primary'
                    : 'border-medium bg-surface-subtle text-secondary hover:border-accent hover:text-primary'
                }`}
              >
                <span>{option}</span>
                <span className={selected ? 'text-accent' : 'text-secondary'}>{CURRENCY_SYMBOLS[option]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor={amountId} className="block text-sm font-medium mb-2 text-primary">
          Monthly amount
        </label>
        <div
          className="flex items-stretch rounded-lg border overflow-hidden transition-colors duration-200 focus-within:border-accent"
          style={{ borderColor: 'rgba(250, 250, 250, 0.12)', backgroundColor: 'rgba(250, 250, 250, 0.05)' }}
        >
          <span className="flex items-center pl-4 text-accent font-semibold" aria-hidden>
            {symbol}
          </span>
          <input
            id={amountId}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Monthly amount in ${currency}`}
            value={amount}
            onChange={(e) => {
              const next = e.target.value.replace(/[^\d]/g, '');
              onAmountChange(next);
            }}
            placeholder="0"
            className="min-w-0 flex-1 px-3 py-3 bg-transparent text-primary focus:outline-none"
          />
          <div className="flex flex-col border-l" style={{ borderColor: 'rgba(250, 250, 250, 0.12)' }}>
            <button
              type="button"
              aria-label="Increase amount"
              onClick={() => nudgeAmount(1)}
              className="flex-1 px-3 text-secondary hover:text-primary hover:bg-primary-subtle transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Decrease amount"
              onClick={() => nudgeAmount(-1)}
              className="flex-1 px-3 text-secondary hover:text-primary hover:bg-primary-subtle transition-colors border-t"
              style={{ borderColor: 'rgba(250, 250, 250, 0.12)' }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const steps: StepDef[] = [
  {
    id: 'intro',
    label: 'Welcome',
    title: "We're opening ChurnSignal to a small group of B2B SaaS teams.",
    subtitle: 'Join the waitlist and tell us a little about your customer base and how you manage churn risk today.',
    validate: () => true,
    render: () => (
      <p className="landing-prose">
        Takes a few minutes, no commitment.
      </p>
    ),
  },
  {
    id: 'contact',
    label: 'About you',
    title: 'Tell us a bit about yourself.',
    subtitle: 'Use a company email or a major provider like Gmail, Outlook, or Yahoo. Company is optional.',
    validate: (a) => a.name.trim().length > 1 && isAcceptedWaitlistEmail(a.email),
    render: ({ answers, setAnswers, emailTouched, setEmailTouched }) => {
      const emailValidation = validateWaitlistEmail(answers.email);
      const emailLooksComplete = /@.+\.[a-z]{2,}/i.test(answers.email.trim());
      const showEmailError =
        (emailTouched || emailLooksComplete) && answers.email.trim().length > 0 && !emailValidation.ok;

      return (
        <div className="space-y-5">
          <TextField
            id="waitlist-name"
            label="Name"
            value={answers.name}
            onChange={(name) => setAnswers({ name })}
            placeholder="Jane Smith"
            required
          />
          <TextField
            id="waitlist-email"
            label="Work email"
            type="email"
            value={answers.email}
            onChange={(email) => setAnswers({ email })}
            onBlur={() => setEmailTouched(true)}
            placeholder="you@company.com"
            required
            error={showEmailError ? emailValidation.message : undefined}
          />
          <TextField
            id="waitlist-company"
            label="Company (optional)"
            value={answers.company}
            onChange={(company) => setAnswers({ company })}
            placeholder="Acme Inc."
          />
        </div>
      );
    },
  },
  {
    id: 'role',
    label: 'About you',
    title: 'Which best describes your role?',
    validate: (a) => Boolean(a.role) && (a.role !== 'Other' || a.roleOther.trim().length > 0),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {ROLE_OPTIONS.map((option) => (
          <OptionButton key={option} selected={answers.role === option} onClick={() => setAnswers({ role: option })}>
            {option}
          </OptionButton>
        ))}
        {answers.role === 'Other' ? (
          <TextField
            id="waitlist-role-other"
            label="Please specify"
            value={answers.roleOther}
            onChange={(roleOther) => setAnswers({ roleOther })}
            placeholder="Your role"
          />
        ) : null}
      </div>
    ),
  },
  {
    id: 'accounts',
    label: 'Customer base',
    title: 'How many paying customer accounts do you manage?',
    validate: (a) => Boolean(a.accountCount),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {ACCOUNT_COUNT_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.accountCount === option}
            onClick={() => setAnswers({ accountCount: option })}
          >
            {option}
          </OptionButton>
        ))}
      </div>
    ),
  },
  {
    id: 'team-size',
    label: 'Customer base',
    title: 'How many people actively manage existing customers?',
    validate: (a) => Boolean(a.teamSize),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {TEAM_SIZE_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.teamSize === option}
            onClick={() => setAnswers({ teamSize: option })}
          >
            {option}
          </OptionButton>
        ))}
      </div>
    ),
  },
  {
    id: 'acv',
    label: 'Customer base',
    title: 'What is your typical annual contract value?',
    validate: (a) => Boolean(a.acv),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {ACV_OPTIONS.map((option) => (
          <OptionButton key={option} selected={answers.acv === option} onClick={() => setAnswers({ acv: option })}>
            {option}
          </OptionButton>
        ))}
      </div>
    ),
  },
  {
    id: 'risk-process',
    label: 'Current process',
    title: 'How do you currently identify customers at risk of churn?',
    subtitle: 'Select all that apply.',
    validate: (a) =>
      a.riskProcesses.length > 0 &&
      (!a.riskProcesses.includes('Other') || a.riskProcessOther.trim().length > 0),
    render: ({ answers, setAnswers, toggleMulti }) => (
      <div className="space-y-3">
        {RISK_PROCESS_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.riskProcesses.includes(option)}
            onClick={() => toggleMulti('riskProcesses', option)}
          >
            {option}
          </OptionButton>
        ))}
        {answers.riskProcesses.includes('Other') ? (
          <TextField
            id="waitlist-risk-other"
            label="Please specify"
            value={answers.riskProcessOther}
            onChange={(riskProcessOther) => setAnswers({ riskProcessOther })}
          />
        ) : null}
      </div>
    ),
  },
  {
    id: 'frustration',
    label: 'Current process',
    title: "What's the biggest frustration you've experienced when trying to manage churn risk?",
    subtitle: 'Please answer in your own words.',
    validate: (a) => a.frustration.trim().length > 0,
    render: ({ answers, setAnswers }) => (
      <TextAreaField
        id="waitlist-frustration"
        label="Your answer"
        value={answers.frustration}
        onChange={(frustration) => setAnswers({ frustration })}
        placeholder="What's hardest about managing churn risk today?"
      />
    ),
  },
  {
    id: 'improvement',
    label: 'Value',
    title: 'Which improvement would create the most value for your team?',
    subtitle: 'Choose one.',
    validate: (a) =>
      Boolean(a.improvement) && (a.improvement !== 'Other' || a.improvementOther.trim().length > 0),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {IMPROVEMENT_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.improvement === option}
            onClick={() => setAnswers({ improvement: option })}
          >
            {option}
          </OptionButton>
        ))}
        {answers.improvement === 'Other' ? (
          <TextField
            id="waitlist-improvement-other"
            label="Please specify"
            value={answers.improvementOther}
            onChange={(improvementOther) => setAnswers({ improvementOther })}
          />
        ) : null}
      </div>
    ),
  },
  {
    id: 'churn-signs',
    label: 'Recent churn',
    title: 'Think about customers who have churned recently.',
    subtitle: 'How often were there signs in their product usage or billing before they cancelled?',
    validate: (a) => Boolean(a.churnSignsFrequency),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {FREQUENCY_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.churnSignsFrequency === option}
            onClick={() => setAnswers({ churnSignsFrequency: option })}
          >
            {option}
          </OptionButton>
        ))}
      </div>
    ),
  },
  {
    id: 'could-act',
    label: 'Recent churn',
    title: 'If you had known earlier, how often could your team realistically have done something about it?',
    validate: (a) => Boolean(a.couldActFrequency),
    render: ({ answers, setAnswers }) => (
      <div className="space-y-3">
        {FREQUENCY_OPTIONS.map((option) => (
          <OptionButton
            key={option}
            selected={answers.couldActFrequency === option}
            onClick={() => setAnswers({ couldActFrequency: option })}
          >
            {option}
          </OptionButton>
        ))}
      </div>
    ),
  },
  {
    id: 'bargain-price',
    label: 'Pricing signal',
    title: 'What would ChurnSignal be worth to you?',
    subtitle:
      'If ChurnSignal helped your team identify meaningful churn risk, understand why a customer was surfaced, and know what action to take, at what monthly price would it feel like a bargain?',
    validate: (a) => a.bargainAmount.trim().length > 0,
    render: ({ answers, setAnswers }) => (
      <PriceFields
        amountId="waitlist-bargain"
        currency={answers.bargainCurrency}
        amount={answers.bargainAmount}
        onCurrencyChange={(bargainCurrency) => setAnswers({ bargainCurrency })}
        onAmountChange={(bargainAmount) => setAnswers({ bargainAmount })}
      />
    ),
  },
  {
    id: 'expensive-price',
    label: 'Pricing signal',
    title: 'At what monthly price would it become too expensive to seriously consider?',
    validate: (a) => a.expensiveAmount.trim().length > 0,
    render: ({ answers, setAnswers }) => (
      <PriceFields
        amountId="waitlist-expensive"
        currency={answers.expensiveCurrency}
        amount={answers.expensiveAmount}
        onCurrencyChange={(expensiveCurrency) => setAnswers({ expensiveCurrency })}
        onAmountChange={(expensiveAmount) => setAnswers({ expensiveAmount })}
      />
    ),
  },
  {
    id: 'anything-else',
    label: 'Anything else',
    title: "Anything else you'd like us to know about how you manage churn risk today?",
    optional: true,
    validate: () => true,
    render: ({ answers, setAnswers }) => (
      <TextAreaField
        id="waitlist-anything-else"
        label="Optional"
        value={answers.anythingElse}
        onChange={(anythingElse) => setAnswers({ anythingElse })}
        placeholder="Optional notes"
      />
    ),
  },
];

export default function WaitlistWizard() {
  const navigate = useNavigate();
  const createInquiry = useCreateInquiry();
  const restoredDraft = useMemo(() => readWaitlistDraft(), []);
  const [stepIndex, setStepIndex] = useState(() => {
    if (!restoredDraft) return 0;
    return Math.min(Math.max(restoredDraft.stepIndex, 0), steps.length - 1);
  });
  const [answers, setAnswersState] = useState<WaitlistAnswers>(
    () => restoredDraft?.answers ?? initialWaitlistAnswers,
  );
  const [resumedFromDraft] = useState(() => Boolean(restoredDraft));
  const [emailTouched, setEmailTouched] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' as ToastType | '' });

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const progress = useMemo(() => ((stepIndex + 1) / steps.length) * 100, [stepIndex]);

  const setAnswers = (patch: Partial<WaitlistAnswers>) => {
    setAnswersState((prev) => ({ ...prev, ...patch }));
  };

  const toggleMulti = (key: 'riskProcesses', value: string) => {
    setAnswersState((prev) => {
      const current = prev[key];
      const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const captchaRequired = isLast && turnstileSiteKey.length > 0;
  const captchaReady = !captchaRequired || turnstileToken.length > 0;
  const canContinue = step.validate(answers) && captchaReady;

  useEffect(() => {
    trackFormStartedOnce();
  }, []);

  useEffect(() => {
    if (!isLast) {
      setTurnstileToken('');
    }
  }, [isLast]);

  const persistStep = (completedStepIndex: number, nextStepIndex: number, nextAnswers: WaitlistAnswers) => {
    const completed = steps[completedStepIndex];
    saveWaitlistDraft(nextStepIndex, nextAnswers);
    void trackWaitlistFunnel({
      eventType: 'step_answered',
      stepId: completed.id,
      stepIndex: completedStepIndex,
      stepLabel: completed.label,
      answers: nextAnswers,
      pagePath: '/waitlist',
    });
  };

  const handleSubmit = () => {
    if (!isAcceptedWaitlistEmail(answers.email)) {
      setEmailTouched(true);
      return;
    }

    const { firstName, lastName } = splitName(answers.name);
    const inquiryData: CreateInquiryRequest = {
      firstName,
      lastName,
      email: answers.email.trim(),
      reason: 'Registration',
      text: formatWaitlistAnswers(answers),
      turnstileToken: turnstileToken || undefined,
      waitlist: toWaitlistLeadFields(answers),
    };

    createInquiry.mutate(inquiryData, {
      onSuccess: () => {
        persistStep(stepIndex, stepIndex, answers);
        void trackWaitlistFunnel({
          eventType: 'form_submitted',
          stepId: step.id,
          stepIndex,
          stepLabel: step.label,
          answers,
          pagePath: '/waitlist',
        });
        clearWaitlistDraft();
        navigate(AppRoutes.waitlistThankYou, {
          replace: true,
          state: { name: answers.name.trim() },
        });
      },
      onError: () => {
        setToastInfo({ message: 'Something went wrong. Please try again.', toastType: 'error' });
        setOpen(true);
      },
    });
  };

  const handleNext = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canContinue || createInquiry.isLoading) {
      return;
    }
    if (isLast) {
      handleSubmit();
      return;
    }
    const nextIndex = Math.min(stepIndex + 1, steps.length - 1);
    persistStep(stepIndex, nextIndex, answers);
    setStepIndex(nextIndex);
  };

  return (
    <>
      {resumedFromDraft ? (
        <p className="text-sm text-secondary mb-4 text-center">
          Continuing where you left off on this device.
        </p>
      ) : null}
      <form onSubmit={handleNext} className="landing-card p-6 sm:p-8 md:p-10">
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4 mb-3">
            <p className="landing-section-label mb-0">{step.label}</p>
            <p className="text-sm text-secondary">
              {stepIndex + 1} / {steps.length}
            </p>
          </div>
          <div className="h-1.5 rounded-full bg-surface-subtle overflow-hidden border border-light">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, backgroundColor: '#00b9ff' }}
            />
          </div>
        </div>

        <h2 className="landing-headline text-2xl md:text-3xl font-bold leading-tight mb-3">{step.title}</h2>
        {step.subtitle ? <p className="landing-prose mb-8">{step.subtitle}</p> : <div className="mb-8" />}

        <div className="mb-10">
          {step.render({ answers, setAnswers, toggleMulti, emailTouched, setEmailTouched })}
          {captchaRequired ? (
            <div className="mt-8">
              <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              const previous = Math.max(stepIndex - 1, 0);
              saveWaitlistDraft(previous, answers);
              setStepIndex(previous);
            }}
            disabled={stepIndex === 0 || createInquiry.isLoading}
            className="px-5 py-3 rounded-lg text-sm font-semibold border border-medium text-secondary hover:text-primary hover:border-accent disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Back
          </button>

          <button
            type="submit"
            disabled={!canContinue || createInquiry.isLoading}
            className="px-8 py-3.5 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
            style={{
              backgroundColor: createInquiry.isLoading ? '#0099cc' : '#fafafa',
              color: '#0e1c2a',
              boxShadow: '0 4px 24px rgba(250, 250, 250, 0.12)',
            }}
          >
            {createInquiry.isLoading
              ? 'Submitting…'
              : isLast
                ? 'Join the early access list'
                : step.optional
                  ? 'Continue'
                  : 'Continue'}
          </button>
        </div>
      </form>

      <div className="fixed top-24 right-4 z-[10000]">
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={(toastInfo.toastType || 'default') as ToastType}
          />
        </ProvideToasts>
      </div>
    </>
  );
}
